<div [hidden]="!ViewReady">
    <h1 class="mat-h1">Categories</h1>
    <div>
        The following table lists the categories defined for use in the IRIS System for categorizing board member requests. You may use
        the tools provided to create a new category entry, change the currently designated cabinet member who handles that category,
        revise category descriptions, or remove unused categories. Please make note of the following important guidelines for this
        editor:
    </div>
    <ul>
        <li>
            Categories which have been used to assign an existing IRIS request are marked with a <strong>padlock</strong> icon and
            <u>cannot</u> be removed from the IRIS System.
        </li>
        <li>
            New categories must have a cabinet member assigned to them. You may use the provided dropdown list of known cabinet members
            to select the cabinet member who is intended to handle the category.
        </li>
        <li>
            <strong
                >All changes made to this page must be saved by clicking the "Save Changes" button or they will be lost when you leave
                this page.</strong
            >
        </li>
    </ul>
    <div fxLayout="row" fxLayoutAlign="space-between center" class="margin-bottom">
        <button type="button" *ngIf="!CategoryPanelShowing" mat-raised-button color="primary" (click)="ShowCategoryForm()">
            Add New Category
        </button>
        <button type="button" mat-raised-button color="accent" (click)="SaveChanges()" [disabled]="!HasAnyChanges">
            <mat-icon *ngIf="HasAnyChanges">cloud_upload</mat-icon>
            <span *ngIf="HasAnyChanges">Save All Changes</span>
            <mat-icon *ngIf="!HasAnyChanges">cloud_done</mat-icon>
            <span *ngIf="!HasAnyChanges">All Updated</span>
        </button>
    </div>
    <form [formGroup]="CategoryForm" novalidate>
        <section [hidden]="!CategoryPanelShowing" class="mat-elevation-z10 categorypanel margin-top margin-bottom">
            <h2 class="mat-h2">New Category Panel</h2>
            <div>
                Please provide the category description and the cabinet member who will be handling requests for this category in the
                spaces provided below:
            </div>
            <div fxLayout="column" fxLayoutAlign="start stretch">
                <div
                    fxLayout="row"
                    fxLayoutAlign="start center"
                    fxLayoutGap.gt-sm="30px"
                    fxLayout.lt-md="column"
                    fxLayoutAlign.lt-md="start stretch"
                >
                    <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="10px" fxFlex.gt-sm="45">
                        <app-form-label [required]="true">Description:</app-form-label>
                        <mat-form-field appearance="outline" fxFlex>
                            <input type="text" matInput maxlength="100" formControlName="Description" #description />
                            <mat-hint align="start">Limit of 100 characters</mat-hint>
                            <mat-hint align="end">{{ CategoryForm?.get('Description')?.value?.length || 0 }}/100</mat-hint>
                            <mat-error *ngIf="CategoryForm?.get('Description')?.hasError('required')">Required</mat-error>
                        </mat-form-field>
                    </div>
                    <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="10px" fxFlex.gt-sm="45">
                        <app-form-label [required]="true">Cabinet Member:</app-form-label>
                        <mat-form-field appearance="outline" fxFlex>
                            <mat-select formControlName="CabinetMember" placeholder="Select cabinet member...">
                                <mat-option [value]="null">Select cabinet member...</mat-option>
                                <mat-option *ngFor="let cab of AllCabinetMembers" [value]="cab"
                                    >{{ cab.FirstName | titleCase }} {{ cab.LastName | titleCase }} ({{
                                        cab.LocalJobTitle
                                    }})</mat-option
                                >
                            </mat-select>
                        </mat-form-field>
                    </div>
                </div>
                <div fxLayout="row" fxLayoutAlign="end center" fxLayoutGap="15px">
                    <button type="button" mat-raised-button color="warn" (click)="ResetForm()">Cancel</button>
                    <button type="button" mat-raised-button color="primary" (click)="AddCategory()" [disabled]="CategoryForm.invalid">
                        Add Category
                    </button>
                </div>
            </div>
        </section>
        <table class="fullwidth mat-table collapsed" *ngIf="!SmallDisplay">
            <thead>
                <tr class="mat-header-row">
                    <th class="mat-header-cell mat-column-InUse centered">Locked?</th>
                    <th class="mat-header-cell mat-column-CategoryDescription">Description</th>
                    <th class="mat-header-cell mat-column-LastName">Cabinet Member</th>
                    <th class="mat-header-cell mat-column-Actions centered">Action(s)</th>
                </tr>
            </thead>
            <tbody formArrayName="Categories">
                <tr
                    *ngFor="let cat of CategoryForm.get('Categories')?.controls; even as $even; index as $index"
                    [formGroup]="cat"
                    class="mat-row"
                    [class.primarybackground50]="$even && !cat.get('Changed')?.value"
                    [class.warnbackground100]="cat.get('Changed')?.value"
                >
                    <td class="centered mat-cell"><mat-icon *ngIf="cat.get('InUse')?.value">lock</mat-icon></td>
                    <td class="mat-cell">
                        <mat-form-field appearance="outline" class="fullwidth" *ngIf="cat.get('ToRemove')?.value !== true">
                            <input type="text" matInput formControlName="CategoryDescription" maxlength="100" />
                            <mat-hint align="start">Limit of 100 characters</mat-hint>
                            <mat-hint align="end">{{ cat?.get('CategoryDescription')?.value?.length || 0 }}/100</mat-hint>
                            <mat-error *ngIf="cat?.get('CategoryDescription')?.hasError('required')">Required</mat-error>
                        </mat-form-field>
                        <div class="toremove" *ngIf="cat.get('ToRemove')?.value">{{ cat?.get('CategoryDescription')?.value }}</div>
                    </td>
                    <td class="mat-cell">
                        <mat-form-field appearance="outline" class="fullwidth" *ngIf="cat.get('ToRemove')?.value !== true">
                            <mat-select formControlName="CabinetMember">
                                <mat-option
                                    *ngFor="let cab of AllCabinetMembers"
                                    [value]="cab"
                                    [style.font-weight]="
                                        cat?.get('OriginalCabinetMember')?.value?.SAPID === cab.SAPID ? 'bold' : 'normal'
                                    "
                                    >{{ cab.FirstName | titleCase }} {{ cab.LastName | titleCase }} ({{
                                        cab.LocalJobTitle
                                    }})</mat-option
                                >
                            </mat-select>
                        </mat-form-field>
                        <div *ngIf="cat.get('ToRemove')?.value" class="toremove">
                            {{ cat.get('CabinetMember')?.value?.FirstName | titleCase }}
                            {{ cat.get('CabinetMember')?.value?.LastName | titleCase }} ({{
                                cat.get('CabinetMember')?.value?.LocalJobTitle
                            }})
                        </div>
                    </td>
                    <td class="centered mat-cell">
                        <div fxLayout="column" fxLayoutAlign="start center" fxLayoutGap="10px" class="margin-top margin-bottom">
                            <button
                                type="button"
                                *ngIf="cat.get('InUse')?.value !== true && cat.get('ToRemove')?.value !== true"
                                mat-raised-button
                                color="warn"
                                (click)="RemoveCategory(cat, $index)"
                            >
                                Remove
                            </button>
                            <button
                                type="button"
                                *ngIf="cat.get('ToRemove')?.value === true"
                                mat-raised-button
                                color="accent"
                                (click)="RestoreCategory(cat)"
                            >
                                Restore
                            </button>
                            <button
                                type="button"
                                *ngIf="cat.get('Changed')?.value && cat.get('CategoryID')?.value != null"
                                mat-raised-button
                                color="accent"
                                (click)="ResetCategory(cat)"
                            >
                                Reset
                            </button>
                        </div>
                    </td>
                </tr>
                <tr *ngIf="NoData" class="mat-row">
                    <td colspan="4" class="mat-cell">
                        <em>There are no categories defined and/or active in the IRIS system at this time.</em>
                    </td>
                </tr>
            </tbody>

            <ng-container matColumnDef="SmallDisplay">
                <th mat-header-cell *matHeaderCellDef>Categories</th>
                <td mat-cell *matCellDef="let cat">
                    <div fxLayout="column" fxLayoutAlign="start stretch">
                        <div fxLayout="column" fxLayoutAlign="start stretch">
                            <div>Description:</div>
                            <div fxLayout="row" fxLayoutAlign="start center">
                                <mat-icon *ngIf="cat.InUse">lock</mat-icon>
                                <div>{{ cat.CategoryDescription }}</div>
                            </div>
                        </div>
                        <div fxLayout="row" fxLayoutAlign="space-between start">
                            <div>Last Name</div>
                            <div>{{ cat?.CabinetMember?.LastName | titleCase }}</div>
                        </div>
                        <div fxLayout="row" fxLayoutAlign="space-between start">
                            <div>First Name</div>
                            <div>{{ cat?.CabinetMember?.FirstName | titleCase }}</div>
                        </div>
                        <div fxLayout="row" fxLayoutAlign="space-between start">
                            <div>Title</div>
                            <div>{{ cat?.CabinetMember?.LocalJobTitle }}</div>
                        </div>
                        <div fxLayout="column" fxLayoutAlign="start center" fxLayoutGap="15px">
                            <button
                                type="button"
                                *ngIf="cat.InUse !== true"
                                mat-raised-button
                                color="warn"
                                (click)="RemoveCategory(cat)"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </td>
            </ng-container>
        </table>

        <div *ngIf="SmallDisplay">
            <div
                *ngFor="let cat of CategoryForm.get('Categories')?.controls; even as $even; index as $index"
                [formGroup]="cat"
                [class.primarybackground50]="$even && !cat.get('Changed')?.value"
                [class.warnbackground100]="cat.get('Changed')?.value"
                fxLayout="column"
                fxLayoutAlign="start stretch"
                class="grayborder thinpadded"
            >
                <div *ngIf="cat.get('InUse')?.value" fxLayout="row" fxLayoutAlign="start start" class="margin-bottom-thin">
                    <mat-icon>lock</mat-icon>
                    <em>This category is in use and cannot be removed.</em>
                </div>
                <div><strong>Description:</strong></div>
                <mat-form-field appearance="outline" class="fullwidth" *ngIf="cat.get('ToRemove')?.value !== true">
                    <input type="text" matInput formControlName="CategoryDescription" maxlength="100" />
                    <mat-hint align="start">Limit of 100 characters</mat-hint>
                    <mat-hint align="end">{{ cat?.get('CategoryDescription')?.value?.length || 0 }}/100</mat-hint>
                    <mat-error *ngIf="cat?.get('CategoryDescription')?.hasError('required')">Required</mat-error>
                </mat-form-field>
                <div class="toremove" *ngIf="cat.get('ToRemove')?.value">{{ cat?.get('CategoryDescription')?.value }}</div>
                <div><strong>Cabinet Member:</strong></div>
                <mat-form-field appearance="outline" class="fullwidth" *ngIf="cat.get('ToRemove')?.value !== true">
                    <mat-select formControlName="CabinetMember">
                        <mat-option
                            *ngFor="let cab of AllCabinetMembers"
                            [value]="cab"
                            [style.font-weight]="cat?.get('OriginalCabinetMember')?.value?.SAPID === cab.SAPID ? 'bold' : 'normal'"
                            >{{ cab.FirstName | titleCase }} {{ cab.LastName | titleCase }} ({{ cab.LocalJobTitle }})</mat-option
                        >
                    </mat-select>
                </mat-form-field>
                <div *ngIf="cat.get('ToRemove')?.value" class="toremove">
                    {{ cat.get('CabinetMember')?.value?.FirstName | titleCase }}
                    {{ cat.get('CabinetMember')?.value?.LastName | titleCase }} ({{ cat.get('CabinetMember')?.value?.LocalJobTitle }})
                </div>
                <div fxLayout="column" fxLayoutAlign="start center" class="margin-top margin-bottom">
                    <button
                        type="button"
                        *ngIf="cat.get('InUse')?.value !== true && cat.get('ToRemove')?.value !== true"
                        mat-raised-button
                        color="warn"
                        (click)="RemoveCategory(cat, $index)"
                    >
                        Remove
                    </button>
                    <button
                        type="button"
                        *ngIf="cat.get('ToRemove')?.value === true"
                        mat-raised-button
                        color="accent"
                        (click)="RestoreCategory(cat)"
                    >
                        Restore
                    </button>
                    <button
                        type="button"
                        *ngIf="cat.get('Changed')?.value && cat.get('CategoryID')?.value != null"
                        mat-raised-button
                        color="accent"
                        (click)="ResetCategory(cat)"
                    >
                        Reset
                    </button>
                </div>
            </div>
        </div>

        <!-- <mat-paginator [pageSizeOptions]="[5, 10, 25, 50]" [pageSize]="10"></mat-paginator> -->
    </form>
</div>
